import type { eventWithTime } from '@rrweb/types'

type ReplayStatus = 'idle' | 'recording' | 'ready'

export type VisualReplaySnapshot = {
  status: ReplayStatus
  events: eventWithTime[]
  stoppedByLimit: boolean
}

type Recorder = (options: {
  emit: (event: eventWithTime) => void
  maskAllInputs: boolean
  blockClass: string
  maskTextClass: string
}) => () => void

type VisualReplayDeps = {
  loadRecorder: () => Promise<{ record: Recorder }>
  setTimeout: (callback: () => void, ms: number) => ReturnType<typeof setTimeout>
  clearTimeout: (timeout: ReturnType<typeof setTimeout>) => void
  maxDurationMs: number
}

const DEFAULT_MAX_DURATION_MS = 10 * 60 * 1000

const defaultDeps: VisualReplayDeps = {
  loadRecorder: () => import('@rrweb/record') as Promise<{ record: Recorder }>,
  setTimeout: (callback, ms) => globalThis.setTimeout(callback, ms),
  clearTimeout: (timeout) => globalThis.clearTimeout(timeout),
  maxDurationMs: DEFAULT_MAX_DURATION_MS,
}

export function createVisualReplayStore(overrides: Partial<VisualReplayDeps> = {}) {
  const deps = { ...defaultDeps, ...overrides }
  const scheduleTimeout = deps.setTimeout
  const cancelTimeout = deps.clearTimeout
  const listeners = new Set<() => void>()
  let snapshot: VisualReplaySnapshot = { status: 'idle', events: [], stoppedByLimit: false }
  let generation = 0
  let stopRecorder: (() => void) | undefined
  let timeout: ReturnType<typeof setTimeout> | undefined

  const emit = () => listeners.forEach((listener) => listener())
  const update = (nextSnapshot: VisualReplaySnapshot) => {
    snapshot = nextSnapshot
    emit()
  }
  const clearRecording = () => {
    if (timeout !== undefined) {
      cancelTimeout(timeout)
      timeout = undefined
    }
    const stop = stopRecorder
    stopRecorder = undefined
    stop?.()
  }

  const stop = (stoppedByLimit = false) => {
    generation += 1
    clearRecording()
    if (snapshot.status !== 'recording') return
    update(snapshot.events.length > 0
      ? { ...snapshot, status: 'ready', stoppedByLimit }
      : { status: 'idle', events: [], stoppedByLimit: false })
  }

  return {
    async start(): Promise<void> {
      stop()
      const currentGeneration = ++generation
      update({ status: 'recording', events: [], stoppedByLimit: false })
      try {
        const { record } = await deps.loadRecorder()
        if (currentGeneration !== generation || snapshot.status !== 'recording') return
        stopRecorder = record({
          emit: (event) => update({ ...snapshot, events: [...snapshot.events, event] }),
          maskAllInputs: true,
          blockClass: 'rr-block',
          maskTextClass: 'rr-mask',
        })
        timeout = scheduleTimeout(() => stop(true), deps.maxDurationMs)
      } catch {
        if (currentGeneration === generation && snapshot.status === 'recording') {
          update({ status: 'idle', events: [], stoppedByLimit: false })
        }
      }
    },
    stop,
    getSnapshot: () => snapshot,
    subscribe(listener: () => void) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

export const visualReplayStore = createVisualReplayStore()
